<template>
    <TprmModal
        additional-class-name="UploadEntityDocumentModal"
        title="Add a File for Reference"
        subtitle="Select a file and assign a tag to organize it."
        open
        size="md"
        allow-close
        @close="$emit('close')"
    >
        <div class="UploadEntityDocumentModal__content">
            <div class="UploadEntityDocumentModal__notice">
                <EidIcon :icon-src="faInfoIcon" class="UploadEntityDocumentModal__noticeIcon" />
                <span>
                    Files added here are for internal reference and context only. They will not be used to
                    verify requirements or determine compliance.
                </span>
            </div>
            <div class="UploadEntityDocumentModal__section">
                <label class="UploadEntityDocumentModal__label">Select File</label>
                <NewFileListInput
                    id="upload-entity-document-input"
                    :value="submissionFiles"
                    :multiple="false"
                    :disabled="submitting"
                    :accepted-extensions="ACCEPTED_FILE_EXTENSIONS"
                    :accepted-mime-types="ACCEPTED_FILE_MIME_TYPES"
                    @input="onFilesChange"
                />
            </div>
            <div v-if="hasInvalidFiles" class="UploadEntityDocumentModal__validationError">
                Please remove invalid files before uploading.
            </div>
            <div class="UploadEntityDocumentModal__section">
                <label class="UploadEntityDocumentModal__label">File Tag</label>
                <TagSelector
                    :rp-name="rpName"
                    resource-type="DOCUMENT"
                    :value="tags"
                    :disabled="submitting"
                    @input="tags = $event"
                />
            </div>
            <div v-if="error" class="UploadEntityDocumentModal__errorBanner">
                {{ error }}
            </div>
        </div>

        <template #footer>
            <NewButton
                variant="outline"
                color="neutral"
                :disabled="submitting"
                @click="$emit('close')"
            >
                Cancel
            </NewButton>
            <NewButton
                color="primary"
                :loading="submitting"
                :disabled="!canUpload"
                @click="submit"
            >
                <EidIcon :icon-src="faUploadIcon" />
                Upload
            </NewButton>
        </template>
    </TprmModal>
</template>

<style lang="scss">
    .UploadEntityDocumentModal {
        &__content {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        &__section {
            display: flex;
            flex-direction: column;
            gap: 6px;
        }

        &__notice {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            padding: 10px 12px;
            border-radius: 6px;
            background-color: var(--eidTechGray-10);
            color: var(--eidTechGray);
            font-size: 12px;
            line-height: 1.4;
        }

        &__noticeIcon {
            flex-shrink: 0;
            margin-top: 1px;
            width: 14px;
            height: 14px;
            color: var(--eidTechGray);
        }

        &__label {
            font-size: var(--text-label-size, 12px);
            font-weight: var(--text-label-weight, 500);
            line-height: var(--text-label-line-height, 16px);
            color: var(--eidTechGray);
        }

        &__validationError,
        &__errorBanner {
            color: var(--feedback-error-color);
            font-size: 13px;
            line-height: 1.35;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faArrowUpFromBracket, faCircleInfo } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { regularExtensions, regularMimeTypes } from '@evidentid/file-utils/mimeTypes';

    import NewButton from '@/components/new-button/NewButton.vue';
    import NewFileListInput from '@/components/new-file-list-input/NewFileListInput.vue';
    import { NewFileListInputFile } from '@/components/new-file-list-input/types';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import TagSelector from '@/modules/entity-tags/components/TagSelector/TagSelector.vue';

    const ACCEPTED_FILE_EXTENSIONS = [ ...regularExtensions, 'docx', 'xlsx', 'pptx' ];
    const ACCEPTED_FILE_MIME_TYPES = [
        ...regularMimeTypes,
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];

    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'submit' but it is
    // neither declared in the emits option nor as an 'onClose'/'onSubmit' prop" every
    // time it fires.
    export default defineComponent({
        name: 'UploadEntityDocumentModal',
        components: {
            NewFileListInput,
            NewButton,
            TprmModal,
            EidIcon,
            TagSelector,
        },
        props: {
            rpName: {
                type: String as PropType<string>,
                required: true,
            },
            submitting: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            error: {
                type: String as PropType<string>,
                default: '',
            },
        },
        emits: [ 'close', 'submit' ],
        data() {
            return {
                faUploadIcon: faArrowUpFromBracket,
                faInfoIcon: faCircleInfo,
                submissionFiles: [] as NewFileListInputFile[],
                tags: [] as string[],
                ACCEPTED_FILE_EXTENSIONS,
                ACCEPTED_FILE_MIME_TYPES,
            };
        },
        computed: {
            hasInvalidFiles(): boolean {
                return this.submissionFiles.some((submissionFile) => Boolean(submissionFile.error));
            },
            validFiles(): File[] {
                return this.submissionFiles
                    .filter((submissionFile) => !submissionFile.error)
                    .map((submissionFile) => submissionFile.file);
            },
            canUpload(): boolean {
                return this.validFiles.length > 0 && !this.hasInvalidFiles;
            },
        },
        methods: {
            onFilesChange(files: NewFileListInputFile[]): void {
                this.submissionFiles = files.slice(0, 1);
            },
            submit(): void {
                const [ file ] = this.validFiles;
                if (!file || this.hasInvalidFiles || this.submitting) {
                    return;
                }
                this.$emit('submit', { file, tags: this.tags });
            },
        },
    });
</script>
